/**
 * How a model's capabilities are DRAWN — the shared renderers behind the
 * capability badges on both admin screens (#1932).
 *
 * Extracted from the Models picker, where #1928 first built them. The Pricing
 * tab needs precisely the same three renderers, and two screens describing the
 * same model differently is the failure this prevents — most sharply for
 * reasoning, where the rule below is genuinely subtle and was wrong once
 * already.
 *
 * A new module rather than a home in an existing one: nothing owned "how a
 * capability is drawn". `agentModelSpec.ts` is spec parsing and facet keys with
 * no JSX, and leaving these on a page component would make the second screen
 * import from a page.
 *
 * Presentation only — no fetching, no capability resolution. The effective
 * value (`override ?? upstream`) is computed server-side and arrives on the
 * row; these functions render what they are given.
 */
import { Badge } from '@/components/ui/badge'
import type { ModelPricingRow } from '@/lib/api'

/** #1928 — render one capability as a badge, in THREE states.
 *
 * Known-supported shows a badge; known-unsupported shows a muted struck badge;
 * unknown shows NOTHING. A positive-badge-only design would render
 * known-unsupported and unknown identically, which defeats the diagnostic
 * purpose — an operator could not tell "this model can't" from "we don't know".
 *
 * Labels are capability NOUNS, matching the shipping `👁 vision enabled`. An
 * earlier draft read "can use tools", which would have forced every other badge
 * into a verb phrase ("knows how to reason") to stay consistent. */
export function CapabilityBadge({ value, label }: { value: boolean | undefined; label: string }) {
  if (value === undefined) return null
  return value
    ? <Badge variant="secondary" className="w-fit whitespace-nowrap text-[10px]">{label}</Badge>
    : (
      <Badge
        variant="secondary"
        className="w-fit whitespace-nowrap text-[10px] line-through opacity-60"
        title="Our records say this model can't do this."
      >
        {label}
      </Badge>
    )
}

/** The reasoning badge, gated on the model ACTUALLY REASONING.
 *
 * `reasoningEffortReducible` answers "can the thinking be turned down?", which
 * is not the same question as "does it think?". OpenRouter derives it from
 * `supported_parameters.includes('reasoning_effort')`, so every ordinary
 * non-reasoning model with a parameter list reports a definitive `false` —
 * and reading reducibility alone labelled those models "reasoning · always
 * on", which is the opposite of true. Caught in review on #1948.
 *
 * So `reasoning === true` is the gate, and reducibility only chooses which of
 * the two badges to show. A reasoning model whose reducibility is unknown gets
 * the plain badge: we know it thinks, we do not know whether that can be
 * turned down, and the qualifier is a claim we cannot make. */
export function reasoningBadge(
  row: Pick<ModelPricingRow, 'reasoning' | 'reasoningEffortReducible'> | undefined,
) {
  if (row?.reasoning !== true) return null
  if (row.reasoningEffortReducible === false) {
    return (
      <Badge
        variant="secondary"
        className="w-fit whitespace-nowrap text-[10px] bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
        title="This model reasons, and its thinking can't be turned down — it may spend its whole output budget thinking."
      >
        🧠 reasoning · always on
      </Badge>
    )
  }
  return <Badge variant="secondary" className="w-fit whitespace-nowrap text-[10px]">🧠 reasoning</Badge>
}

/** Context size, shown beside the cost pill. A real selection criterion rather
 * than trivia: the generator compacts its context when it runs out of room,
 * which costs tokens and loses fidelity, so headroom is worth seeing. Absent
 * when the catalog does not publish it — never a fabricated zero. */
export function formatContextSize(tokens: number | undefined): string | null {
  if (typeof tokens !== 'number' || !Number.isFinite(tokens) || tokens <= 0) return null
  if (tokens >= 1_000_000) {
    const m = tokens / 1_000_000
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M`
  }
  return `${Math.round(tokens / 1000)}K`
}

/** The same magnitude with the word attached, for places that have no column
 * header to supply it — the model picker, where a bare "200K" beside a cost
 * pill would not say what it measures. Under a column already headed Context
 * the word is redundant, and long enough to wrap the cell onto two lines. */
export function formatContextWindow(tokens: number | undefined): string | null {
  const size = formatContextSize(tokens)
  return size === null ? null : `${size} context`
}
